import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import {
  AssetsEntity,
  OrganizationEntity,
  ParticipantEntity,
} from "./AppProfileExtraEntity";
import { SystemUserResponseEntity } from "features/systemUserManagement/domain/entities/SystemUserResponseEntity";
import { strings } from "utils/localizedStrings";

export class AppProfileResponseEntity extends BaseUserResponseModel {
  createdAt?: string;
  updatedAt?: string;
  id?: string;
  name?: string;
  code?: string;
  providerCode?: number;
  providerPreviewCode?: string;
  type?: number;
  status?: number;
  expriedSignDate?: string;
  expriedAt?: string;
  isVerified?: boolean;
  organization?: OrganizationEntity;
  template?: string;
  participants?: ParticipantEntity[];
  userContracts?: ParticipantEntity[];
  file?: AssetsEntity;
  reason?: string;
  approvedBy?: SystemUserResponseEntity;

  constructor(data?: Partial<AppProfileResponseEntity>) {
    super(data);
    Object.assign(this, data);
  }

  combineImages?(): AssetsEntity[] {
    const files = this.collectAllFiles();
    const grouped = this.groupByClassify(files);

    return this.buildLabeledImages(grouped);
  }

  collectAllFiles?(): (AssetsEntity & { phoneNumber?: string })[] {
    const allFiles: (AssetsEntity & { phoneNumber?: string })[] = [];

    this.collectBusinessCerts?.(allFiles);
    this.collectParticipantFiles?.(allFiles);
    this.collectMainFile?.(allFiles);

    return allFiles;
  }

  collectBusinessCerts?(
    target: (AssetsEntity & { phoneNumber?: string })[],
  ) {
    this.organization?.businessCerts?.forEach((item) => {
      target.push({ ...item });
    });
  }

  collectParticipantFiles?(
    target: (AssetsEntity & { phoneNumber?: string })[],
  ) {
    this.participants?.forEach((user) => {
      user?.files?.forEach((file) => {
        target.push({
          ...file,
          phoneNumber: user.phoneNumber,
        });
      });
    });
  }

  collectMainFile?(
    target: (AssetsEntity & { phoneNumber?: string })[],
  ) {
    if (!this.file) return;

    target.push({
      ...this.file,
      phoneNumber: this.participants?.[0]?.phoneNumber,
    });
  }

  groupByClassify?(
    files: (AssetsEntity & { phoneNumber?: string })[],
  ): Record<number, (AssetsEntity & { phoneNumber?: string })[]> {
    return files.reduce(
      (acc, file) => {
        const key = file.classify ?? 0;

        if (!acc[key]) {
          acc[key] = [];
        }

        acc[key].push(file);
        return acc;
      },
      {} as Record<number, (AssetsEntity & { phoneNumber?: string })[]>,
    );
  }

  buildLabeledImages?(
    grouped: Record<number, (AssetsEntity & { phoneNumber?: string })[]>,
  ): AssetsEntity[] {
    const images: AssetsEntity[] = [];

    Object.entries(grouped).forEach(([classify, files]) => {
      const total = files.length;

      files.forEach((file, index) => {
        images.push({
          ...file,
          title: this.buildTitle?.(
            Number(classify),
            file.phoneNumber,
            index,
            total,
          ),
        });
      });
    });

    return images;
  }

  buildTitle?(
    classify: number,
    phoneNumber?: string,
    index?: number,
    total?: number,
  ): string {
    const base = this.getAssetsLabel?.(classify);

    const phonePart = phoneNumber ? ` - ${phoneNumber}` : "";
    const indexPart =
      total && total > 1 ? ` (${(index ?? 0) + 1}/${total})` : "";

    return `${base}${phonePart}${indexPart}`;
  }

  getAssetsLabel?(classify?: number) {
    switch (classify) {
      case 1:
        return strings.AppProfileRegister.Labels.AssetsClassify.Avatar;
      case 2:
        return strings.AppProfileRegister.Labels.AssetsClassify.IdCard;
      case 3:
        return strings.AppProfileRegister.Labels.AssetsClassify.Contract;
      case 4:
        return strings.AppProfileRegister.Labels.AssetsClassify.BusinessCerts;
      default:
        return strings.AppProfileRegister.Labels.AssetsClassify.Default;
    }
  }
}
